import type { Theme } from "@mui/material/styles";
import { match } from "ts-pattern";

import type {
  BaseFormTemplate,
  ExtendedThalamosUser,
  ExtendedWorkItem,
  FormContextStatus,
  FormContextType,
  WorkItemMiniForm,
} from "@thalamos/aspire-common";
import {
  applicationTemplateIds,
  formIsVoid,
  getBaseFormTemplate,
  getLatestVersion,
  isCqcUserSession,
  isSoadUserSession,
  recordOfDetentionTemplateIds,
} from "@thalamos/aspire-common";

import {
  hasAnyDoctorDecidedNoMedRec,
  hasNoApplicationDecided,
  hasNoMedRecAutoClosed,
} from "../../pages/FormProgressPage/helpers/formContextLogic.js";

const legalScrutinyMarkedAsComplete = "Legal scrutiny marked as complete";
const awaitingLegalScrutiny = "Awaiting legal scrutiny";

export type WorkItemStatusSummary = {
  statusText: string;
  shortStatusText?: string;
  colour: string;
  textColour?: string;
  tooltip?: string;
  secondaryPill?: {
    text: string;
    shortText: string;
    colour: string;
    textColour: string;
  };
};

/**
 * The Summary pill for one form judged only on its own signatures. Status that
 * belongs to the surrounding context rather than to the form is the caller's to
 * apply, and outranks this.
 */
export function calculateFormSignatureStatus(
  theme: Theme,
  form: Pick<WorkItemMiniForm, "status" | "versions" | "latestVersion">,
  template: Pick<BaseFormTemplate, "parts">,
): WorkItemStatusSummary {
  const signatureCount = getLatestVersion(form).signatures.length;
  const statusText = `${String(signatureCount)} / ${String(template.parts.length)} signatures`;

  return (
    match(form.status)
      .with("finalised", () => ({
        statusText: legalScrutinyMarkedAsComplete,
        colour: theme.palette.statuses.positive,
      }))
      .with("complete", () => ({
        statusText,
        colour: theme.palette.statuses.positive,
      }))
      // A deleted form has no look of its own; report it like one still in progress.
      .with("in-progress", "deleted", () => ({
        statusText,
        colour:
          signatureCount === 0
            ? theme.palette.common.lightGray
            : theme.palette.statuses.pending,
      }))
      .exhaustive()
  );
}

// Ordered least to most progressed, so a context is reported by the form with
// the furthest still to go.
const FORM_PROGRESSION: WorkItemMiniForm["status"][] = [
  "in-progress",
  "complete",
  "finalised",
];

const leastProgressedForm = (
  forms: WorkItemMiniForm[],
): WorkItemMiniForm | undefined =>
  forms
    .filter((f) => f.status !== "deleted")
    .sort(
      (a, b) =>
        FORM_PROGRESSION.indexOf(a.status) - FORM_PROGRESSION.indexOf(b.status),
    )[0];

const buildNoMedRecPill = (theme: Theme) => ({
  text: "No med rec decided",
  shortText: "No med rec decided",
  colour: theme.palette.error.main,
  textColour: theme.palette.common.white,
});

const buildNoApplicationStatus = (theme: Theme): WorkItemStatusSummary => ({
  statusText: "Outcome: Non-detention decision",
  shortStatusText: "Outcome: Non-detention decision",
  colour: theme.palette.error.main,
  textColour: theme.palette.common.white,
});

const buildNoMedRecAutoClosedStatus = (
  theme: Theme,
): WorkItemStatusSummary => ({
  statusText: "Outcome: Non-detention decision",
  shortStatusText: "Outcome: Non-detention decision",
  colour: theme.palette.error.main,
  textColour: theme.palette.common.white,
});

function calculateSoadWorkItemStatus(
  theme: Theme,
  forms: WorkItemMiniForm[],
  otherWorkItems: ExtendedWorkItem["otherWorkItems"],
  user?: ExtendedThalamosUser,
): { statusText: string; colour: string; textColour?: string } {
  const isSoad = isSoadUserSession(user);
  const isCqc = isCqcUserSession(user);
  const isSoadOrCqc = isSoad || isCqc;

  const hasProviderAccessed = otherWorkItems.some(
    (i) =>
      !["soad", "cqc"].includes(i.team.type) &&
      ["accepted", "completed"].includes(i.status),
  );
  const hasProviderDeclined = otherWorkItems.some(
    (i) => !["soad", "cqc"].includes(i.team.type) && i.status === "rejected",
  );

  const soadReportForm = forms.find(
    (f) => f.formTemplate.id === "soad-report-form",
  );

  if (!soadReportForm) {
    return {
      statusText: "SOAD assessment",
      colour: theme.palette.common.lightGray,
    };
  }

  if (soadReportForm.status === "in-progress") {
    return {
      statusText:
        soadReportForm.latestVersion > 1 ? "Amends in Progress" : "In Progress",
      colour: theme.palette.statuses.pending,
    };
  }

  if (soadReportForm.status === "finalised") {
    return {
      statusText: legalScrutinyMarkedAsComplete,
      colour: theme.palette.statuses.positive,
    };
  }

  if (soadReportForm.status === "complete") {
    if (hasProviderAccessed) {
      if (isSoadOrCqc || !user) {
        return {
          statusText: `Accessed by Provider${soadReportForm.latestVersion > 1 ? " (Amended)" : ""}`,
          colour: theme.palette.statuses.positive,
        };
      } else {
        return {
          statusText: `${awaitingLegalScrutiny}${soadReportForm.latestVersion > 1 ? " (Amended)" : ""}`,
          colour: theme.palette.statuses.positive,
        };
      }
    }

    if (hasProviderDeclined) {
      return {
        statusText: "Declined by Provider",
        colour: theme.palette.error.darker,
      };
    }

    if (isSoadOrCqc || !user) {
      return {
        statusText: `Submitted${soadReportForm.latestVersion > 1 ? " (Amended)" : ""}`,
        colour: theme.palette.primary.main,
        textColour: theme.palette.common.white,
      };
    } else {
      return {
        statusText: `${awaitingLegalScrutiny}${soadReportForm.latestVersion > 1 ? " (Amended)" : ""}`,
        colour: theme.palette.statuses.positive,
      };
    }
  }

  return {
    statusText: "SOAD assessment",
    colour: theme.palette.common.lightGray,
  };
}

export function calculateWorkItemStatus(
  theme: Theme,
  item: {
    formContextType: FormContextType;
    forms: WorkItemMiniForm[];
    otherWorkItems: ExtendedWorkItem["otherWorkItems"];
    formContextStatus?: FormContextStatus;
    closedByUserId?: string;
    closedByTeamId?: string;
    closedReason?: string;
  },
  user?: ExtendedThalamosUser,
): WorkItemStatusSummary {
  // Redo (non-s15): a voided form keeps status = "complete" but is superseded —
  // it must never drive status (e.g. count as a signed med rec / application).
  const forms = item.forms.filter((f) => !formIsVoid(f));

  // Once an application is signed, the No Med Rec pill is suppressed: the
  // admission is proceeding to detention, so the non-detention pill is
  // misleading. The participant reasoning remains visible via the
  // participants list.
  const signedApplication = forms.some(
    (f) =>
      ["complete", "finalised"].includes(f.status) &&
      applicationTemplateIds.includes(f.formTemplate.id),
  );

  const withNoMedRecPill = (
    summary: WorkItemStatusSummary,
  ): WorkItemStatusSummary =>
    hasAnyDoctorDecidedNoMedRec(item.otherWorkItems) && !signedApplication
      ? { ...summary, secondaryPill: buildNoMedRecPill(theme) }
      : summary;

  const hasMhaActiveWorkItem = item.otherWorkItems.some(
    (i) =>
      i.team.type === "mha" &&
      ["pending", "accepted", "completed"].includes(i.status),
  );

  const hasActiveDeMinimis = item.otherWorkItems.some(
    (i) =>
      i.requestType === "de-minimis" &&
      (i.status === "pending" || i.status === "accepted"),
  );

  const noApplicationDecided = hasNoApplicationDecided({
    status: item.formContextStatus,
    closedByUserId: item.closedByUserId,
    closedByTeamId: item.closedByTeamId,
  });

  const noMedRecAutoClosed = hasNoMedRecAutoClosed({
    status: item.formContextStatus,
    closedReason: item.closedReason,
  });

  if (item.formContextType === "police-incident") {
    return {
      statusText: "Police incident",
      colour: theme.palette.statuses.positive,
    };
  }

  if (item.formContextType === "soad-assessment") {
    return calculateSoadWorkItemStatus(theme, forms, item.otherWorkItems, user);
  }

  if (
    item.formContextType === "standalone" ||
    item.formContextType === "mca-assessment"
  ) {
    // An MCA assessment may hold two forms and is only as far along as its
    // least-progressed one, so that form speaks for the whole assessment.
    const form =
      item.formContextType === "mca-assessment"
        ? leastProgressedForm(forms)
        : forms[0];

    // `forms` excludes voided forms, so a standalone item whose only form was
    // voided leaves this empty — guard before reading the form's signatures.
    if (!form) {
      return withNoMedRecPill({
        statusText: "No forms signed",
        colour: theme.palette.common.lightGray,
      });
    }
    const template = getBaseFormTemplate(form);
    if (!template) {
      throw new Error(
        `No base form template for ${form.formTemplate.id} version ${form.formTemplate.version}`,
      );
    }

    // A de minimis correction or an outstanding legal scrutiny belongs to the
    // context rather than the form, so for a completed form either outranks the
    // form's own signature count.
    if (form.status === "complete") {
      if (hasActiveDeMinimis) {
        return {
          statusText: "De minimis correction in progress",
          colour: theme.palette.statuses.pending,
        };
      }
      if (hasMhaActiveWorkItem) {
        return {
          statusText: awaitingLegalScrutiny,
          colour: theme.palette.statuses.positive,
        };
      }
    }

    return calculateFormSignatureStatus(theme, form, template);
  }

  if (noMedRecAutoClosed) {
    return buildNoMedRecAutoClosedStatus(theme);
  }

  if (noApplicationDecided) {
    return buildNoApplicationStatus(theme);
  }

  const signedForms =
    forms.filter((f) => ["complete", "finalised"].includes(f.status)).length >
    0;

  const signedRecordOfDetention = forms.find(
    (f) =>
      ["complete", "finalised"].includes(f.status) &&
      recordOfDetentionTemplateIds.includes(f.formTemplate.id),
  );

  const finalised = signedRecordOfDetention?.status === "finalised";

  if (finalised) {
    return withNoMedRecPill({
      statusText: legalScrutinyMarkedAsComplete,
      colour: theme.palette.statuses.positive,
    });
  }

  if (signedRecordOfDetention) {
    if (hasActiveDeMinimis) {
      return withNoMedRecPill({
        statusText: "De minimis correction in progress",
        colour: theme.palette.statuses.pending,
      });
    }
    if (hasMhaActiveWorkItem) {
      return withNoMedRecPill({
        statusText: awaitingLegalScrutiny,
        colour: theme.palette.statuses.positive,
      });
    } else {
      return withNoMedRecPill({
        statusText: "Record of detention signed",
        colour: theme.palette.statuses.positive,
      });
    }
  }

  if (signedApplication) {
    return withNoMedRecPill({
      statusText: "Awaiting record of detention",
      colour: theme.palette.statuses.pending,
    });
  }

  if (signedForms) {
    return withNoMedRecPill({
      statusText: "Awaiting medical recommendations",
      colour: theme.palette.statuses.pending,
    });
  }

  return withNoMedRecPill({
    statusText: "No forms signed",
    colour: theme.palette.common.lightGray,
  });
}
